import { useState, useEffect, CSSProperties } from 'react';
import { Input, Select } from '@mantine/core';
import {
  isValidPhoneNumber,
  parsePhoneNumberFromString,
  CountryCode,
  getCountryCallingCode,
  getCountries,
} from 'libphonenumber-js';
import { useTranslation } from 'react-i18next';

// Codigos repetidos
const PREFERRED_COUNTRIES: Record<string, CountryCode> = {
  '+1': 'US',
  '+61': 'AU',
  '+599': 'BQ',
  '+358': 'FI',
  '+44': 'GB',
  '+590': 'MF',
  '+212': 'MA',
  '+47': 'NO',
  '+290': 'TA',
  '+39': 'IT',
  '+262': 'RE',
};

const getFlagEmoji = (countryCode: CountryCode) => {
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map((char) => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

// Obtener codigos de paises unicos
export const getUniqueCallingCodeOptions = () => {
  const allCountries = getCountries() as CountryCode[];
  const uniqueOptions: Array<{
    value: string; // ej. "+52"
    label: string; // ej. "🇲🇽 +52 (MX)"
    countryCode: CountryCode; // ej. "MX"
  }> = [];

  const usedCallingCodes = new Set<string>();

  allCountries.forEach((country) => {
    try {
      const callingCode = `+${getCountryCallingCode(country)}`;

      // Si ya tenemos este código de llamada
      if (usedCallingCodes.has(callingCode)) {
        if (PREFERRED_COUNTRIES[callingCode] === country) {
          const index = uniqueOptions.findIndex((opt) => opt.value === callingCode);
          if (index >= 0) {
            uniqueOptions[index] = {
              value: callingCode,
              label: `${getFlagEmoji(country)} ${callingCode} (${country})`,
              countryCode: country,
            };
          }
        }
        return;
      }

      // Para códigos nuevos
      usedCallingCodes.add(callingCode);
      uniqueOptions.push({
        value: callingCode,
        label: `${getFlagEmoji(country)} ${callingCode} (${country})`,
        countryCode: country,
      });
    } catch {
      // No hacer nada cuando un ocidgo de pais falla
    }
  });

  // Ordenar alfabéticamente por código de pais
  return uniqueOptions.sort((a, b) => a.countryCode.localeCompare(b.countryCode));
};

interface PhoneInputProps {
  label?: string;
  description?: string;
  defaultValue?: string;
  defaultCallingCode?: string;
  onChange?: (fullNumber: string, callingCode: string, isValid: boolean) => void;
  style?: CSSProperties;
  disabled?: boolean | undefined;
  defaultError?: string | undefined;
  size?: string | undefined;
}

export const PhoneInput: React.FC<PhoneInputProps> = ({
  label = 'Phone number',
  description = '',
  defaultValue = '',
  defaultCallingCode = '+52',
  onChange,
  style,
  disabled,
  defaultError = '',
  size = 'sm',
}) => {
  const [callingCode, setCallingCode] = useState(defaultCallingCode);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [error, setError] = useState('');
  const [isValid, setIsValid] = useState(false);

  const { t } = useTranslation();

  const countryOptions = getUniqueCallingCodeOptions();

  const getCurrentCountryCode = (): CountryCode | undefined =>
    countryOptions.find((opt) => opt.value === callingCode)?.countryCode;

  useEffect(() => {
    if (defaultValue) {
      try {
        const parsed = parsePhoneNumberFromString(defaultValue);
        if (parsed) {
          const code = `+${parsed.countryCallingCode}`;
          if (countryOptions.some((opt) => opt.value === code)) {
            setCallingCode(code);
            setPhoneNumber(parsed.nationalNumber);
          }
        }
      } catch (e) {
        console.warn('Error parsing phone number:', e);
      }
    }
  }, [defaultValue]);

  // Validación y notificación de cambios
  useEffect(() => {
    const validateAndNotify = () => {
      if (!phoneNumber) {
        setError('');
        setIsValid(false);
        onChange?.('', callingCode, false);
        return;
      }

      const country = getCurrentCountryCode();
      if (!country) {
        setError('Invalid country code');
        setIsValid(false);
        onChange?.('', callingCode, false);
        return;
      }

      try {
        const fullNumber = `${callingCode}${phoneNumber}`;
        const isValidPhone = isValidPhoneNumber(fullNumber, country);

        setError(isValidPhone ? '' : 'Invalid phone number');
        setIsValid(isValidPhone);
        onChange?.(fullNumber, callingCode, isValidPhone);
      } catch {
        setError('Invalid phone number');
        setIsValid(false);
        onChange?.('', callingCode, false);
      }
    };

    validateAndNotify();
  }, [callingCode, phoneNumber]);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    const fullNumber = `${callingCode.slice(1)}${value}`;
    if (fullNumber.length <= 15) {
      setPhoneNumber(value);
    }
  };

  const handleCallingCodeChange = (value: string | null) => {
    if (value) {
      setCallingCode(value);
    }
  };

  const inputStyles = {
    borderColor: phoneNumber ? (isValid ? 'green' : 'red') : undefined,
    '&:focus': {
      borderColor: phoneNumber ? (isValid ? 'green' : 'red') : undefined,
    },
  };

  return (
    <div style={style}>
      <Input.Wrapper
        label={label}
        description={description}
        error={defaultError || error}
        size={size}
      >
        <div style={{ display: 'flex', gap: '8px' }}>
          <Select
            data={countryOptions}
            value={callingCode}
            onChange={handleCallingCodeChange}
            searchable
            placeholder="Country code"
            style={{ width: size === 'lg' ? '170px' : '135px' }}
            disabled={disabled}
            size={size}
          />
          <Input
            placeholder={t('save_booking_page.fill_data.phone_placeholder')}
            value={phoneNumber}
            onChange={handlePhoneChange}
            style={{ flex: 1, ...inputStyles }}
            disabled={disabled}
            size={size}
          />
        </div>
      </Input.Wrapper>
    </div>
  );
};
